import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { MdChevronLeft } from 'react-icons/md';
import { FaMapPin } from 'react-icons/fa';
import axiosPost from '../helpers/axios-helper';
import { VENUE_TYPES, slugify, parseList, hasText } from '../constants/venueTypes';
import '../styles/VenueReview.css';

/**
 * A venue's review, interview or menu, on its own page.
 *
 * One component for all three kinds and all eight venue types: they share a
 * shape — headline, opening line, body, and the two boxes beside it — and the
 * server already returns them in one form. The kind only changes the wording
 * and whether a PDF is offered.
 *
 * Routes: /:type/:slug/review | /interview | /menu
 */

const KIND_LABEL = {
  review: 'Our review',
  interview: 'Interview',
  menu: 'Menu',
};

const formatDate = (unix) => {
  const n = Number(unix || 0);
  if (!n) return '';
  const d = new Date(n * 1000);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

export default function VenueFeature({ type = 'hotel', kind = 'review' }) {
  const { slug } = useParams();
  const navigate = useNavigate();

  const spec = VENUE_TYPES[type] || VENUE_TYPES.hotel;

  const [venue, setVenue] = useState(null);
  const [feature, setFeature] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | missing

  // Resolve the slug to an id, the same way the venue pages do
  useEffect(() => {
    if (!slug) { setStatus('missing'); return undefined; }
    const controller = new AbortController();
    axiosPost(
      spec.listEndpoint,
      { 'Content-Type': 'application/json' },
      { page: 1, per_page: 1000 },
      (resp) => {
        const items = Array.isArray(resp?.data?.items) ? resp.data.items : [];
        const found = items.find(it => slugify(it.title || '') === slug);
        if (found?.id) setVenue(found);
        else setStatus('missing');
      },
      controller
    );
    return () => controller.abort();
  }, [slug, spec.listEndpoint]);

  useEffect(() => {
    if (!venue?.id) return undefined;
    const controller = new AbortController();
    axiosPost(
      'features/get',
      { 'Content-Type': 'application/json' },
      { item_type: spec.itemType, item_id: venue.id, kind },
      (resp) => {
        if (resp?.success && resp.data.feature) {
          setFeature(resp.data.feature);
          setStatus('ready');
        } else {
          setStatus('missing');
        }
      },
      controller
    );
    return () => controller.abort();
  }, [venue, spec.itemType, kind]);

  const venuePath = `/${spec.path}/${slug || ''}`;

  // Nothing published here, so hand the reader back to the venue
  useEffect(() => {
    if (status === 'missing') navigate(venuePath, { replace: true });
  }, [status, navigate, venuePath]);

  useEffect(() => {
    if (venue?.title && feature) {
      document.title = `${KIND_LABEL[kind]} — ${venue.title} | OutCaribbean`;
    }
  }, [venue, feature, kind]);

  if (status !== 'ready' || !feature) {
    return (
      <div className='vreview-loading'>
        <div className='vreview-spinner' />
      </div>
    );
  }

  const cover = feature.image || venue.image || '';
  const highlights = parseList(feature.highlight_items);
  const notes = parseList(feature.notes_items);
  const publishedOn = formatDate(feature.published_at);
  const address = venue.address || venue.contact?.address || '';
  const hasAside = highlights.length > 0 || notes.length > 0;
  const bodyWritten = hasText(feature.content);
  const hasMainColumn = bodyWritten || !!feature.file_url;

  return (
    <div className='vreview-page'>
      {cover && (
        <div className='vreview-cover' style={{ backgroundImage: `url('${cover}')` }} />
      )}

      <div className='vreview-shell'>
        <header className={`vreview-head ${bodyWritten ? '' : 'lead-only'}`}>
          <Link to={venuePath} className='vreview-back'>
            <MdChevronLeft size={18} />
            Back to {venue.title}
          </Link>

          <p className='vreview-eyebrow'>{KIND_LABEL[kind]}</p>
          <h1 className='vreview-headline'>{feature.title || venue.title}</h1>

          {feature.standfirst && (
            <p className='vreview-standfirst'>{feature.standfirst}</p>
          )}

          <div className='vreview-meta'>
            {feature.author && <span>By {feature.author}</span>}
            {publishedOn && <span>{publishedOn}</span>}
            {address && (
              <span className='vreview-meta-place'>
                <FaMapPin size={11} />
                {address}
              </span>
            )}
          </div>
        </header>

        <div className={`vreview-body-grid ${hasAside ? '' : 'no-aside'} ${hasMainColumn ? '' : 'no-body'} ${feature.file_url ? 'has-doc' : ''}`}>
          {feature.file_url && (
            <section className='vreview-doc'>
              {/* <object> rather than <iframe>: a browser that cannot display a
                  PDF inline renders the fallback inside it instead of an empty
                  grey box. iOS in particular refuses to embed one. */}
              <object
                className='vreview-doc-frame'
                data={feature.file_url}
                type='application/pdf'
                aria-label={feature.file_name || 'Menu'}
              >
                <div className='vreview-doc-fallback'>
                  <p>Your browser cannot show this document inline.</p>
                  <a href={feature.file_url} target='_blank' rel='noreferrer'>
                    Open it in a new tab
                  </a>
                </div>
              </object>

              <div className='vreview-doc-actions'>
                <a href={feature.file_url} target='_blank' rel='noreferrer'>
                  Open in a new tab
                </a>
                <a href={feature.file_url} download>
                  Download{feature.file_name ? ` (${feature.file_name})` : ''}
                </a>
              </div>
            </section>
          )}

          {/* With no body the opening line in the header is the whole piece */}
          {bodyWritten && (
            <article
              className='vreview-body'
              dangerouslySetInnerHTML={{ __html: feature.content }}
            />
          )}

          {hasAside && (
            <aside className='vreview-aside'>
              {highlights.length > 0 && (
                <div className='vreview-list'>
                  <h2>{feature.highlight_title}</h2>
                  <ul>
                    {highlights.map((item, i) => <li key={i}>{item}</li>)}
                  </ul>
                </div>
              )}

              {notes.length > 0 && (
                <div className='vreview-list vreview-list-notes'>
                  <h2>{feature.notes_title}</h2>
                  <ul>
                    {notes.map((item, i) => <li key={i}>{item}</li>)}
                  </ul>
                </div>
              )}
            </aside>
          )}
        </div>

        <footer className='vreview-foot'>
          {kind === 'review' && (
            <p className='vreview-foot-note'>
              Reviews are written by OutCaribbean. We tell you what we found, the good
              and the awkward, so you can decide for yourself.
            </p>
          )}

          <div className='vreview-foot-actions'>
            <Link to={venuePath} className='vreview-btn-primary'>
              See {venue.title}
            </Link>

            {(venue.website || venue.contact?.website) && (
              <a
                className='vreview-btn-ghost'
                href={venue.website || venue.contact?.website}
                target='_blank'
                rel='noreferrer'
              >
                Visit website
              </a>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}
